// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import ActionButtons from '../components/ActionButtons';
import { buildSchemas } from '../validation/schemas';
import notify from '../utils/toast';

const STATUS_COLORS = {
    planning: 'secondary',
    ongoing: 'primary',
    completed: 'success',
    handed_over: 'dark',
};

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const { can } = useAuth();
    const { t, td} = useI18n();
    const schemas = buildSchemas(t);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schemas.project),
        defaultValues: {
            name: '',
            code: '',
            location: '',
            address: '',
            description: '',
            status: 'planning',
        },
    });

    const load = () => {
        setLoading(true);
        api.get('/projects')
            .then((res) => setProjects(res.data.data))
            .catch((e) => notify.apiError(e, t('failed')))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const closeProjectModal = () => {
        setShowModal(false);
        setEditItem(null);
        reset({
            name: '',
            code: '',
            location: '',
            address: '',
            description: '',
            status: 'planning',
        });
    };

    const openCreate = () => {
        setEditItem(null);
        reset({
            name: '',
            code: '',
            location: '',
            address: '',
            description: '',
            status: 'planning',
        });
        setShowModal(true);
    };

    const openEdit = (p) => {
        setEditItem(p);
        reset({
            name: p.name || '',
            code: p.code || '',
            location: p.location || '',
            address: p.address || '',
            description: p.description || '',
            status: p.status || 'planning',
        });
        setShowModal(true);
    };

    const onSubmit = async (data) => {
        try {
            if (editItem) {
                await api.put(`/projects/${editItem.id}`, data);
                notify.success(t('successUpdate'));
            } else {
                await api.post('/projects', data);
                notify.success(t('successCreate'));
            }
            closeProjectModal();
            load();
        } catch (err) {
            notify.apiError(err, t('failed'));
        }
    };

    const handleDelete = async (p) => {
        if (!window.confirm(t('confirmDelete'))) return;
        try {
            await api.delete(`/projects/${p.id}`);
            notify.success(t('successDelete'));
            load();
        } catch (err) {
            notify.apiError(err, t('failed'));
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">{t('projects')}</h4>
                    <p className="text-muted mb-0">{t('manageProjects')}</p>
                </div>
                {can('projects.create') && (
                    <button className="btn btn-primary" onClick={openCreate}>
                        <i className="bi bi-plus-lg me-1" />
                        {t('newProject')}
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-success" />
                    <div className="text-muted small mt-2">{t('loading')}</div>
                </div>
            ) : (
                <div className="row g-3">
                    {projects.map((p) => (
                        <div className="col-md-6 col-xl-4" key={p.id}>
                            <div className="card table-card h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <h5 className="card-title mb-1">{p.name}</h5>
                                        <span className={`badge bg-${STATUS_COLORS[p.status] || 'secondary'}`}>
                                            {td('status', p.status)}
                                        </span>
                                    </div>
                                    <div className="small text-muted mb-2">{p.code}</div>
                                    <p className="small mb-2">
                                        <i className="bi bi-geo-alt me-1" />
                                        {p.location || '—'}
                                    </p>
                                    <ActionButtons
                                        viewTo={`/projects/${p.id}`}
                                        canView={can('projects.view')}
                                        canEdit={can('projects.edit')}
                                        canDelete={can('projects.delete')}
                                        onEdit={() => openEdit(p)}
                                        onDelete={() => handleDelete(p)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    {projects.length === 0 && (
                        <div className="col-12 text-center text-muted py-5">{t('noData')}</div>
                    )}
                </div>
            )}

            {showModal && (
                <div
                    className="modal show d-block app-modal"
                    style={{ background: 'rgba(0,0,0,.5)' }}
                    onClick={closeProjectModal}
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <form className="modal-content" onSubmit={handleSubmit(onSubmit)} noValidate>
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editItem ? t('editProject') : t('newProject')}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeProjectModal} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-2">
                                    <label className="form-label">{t('name')}</label>
                                    <input
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        {...register('name')}
                                    />
                                    {errors.name && (
                                        <div className="invalid-feedback d-block">{errors.name.message}</div>
                                    )}
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('code')}</label>
                                    <input className="form-control" {...register('code')} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('location')}</label>
                                    <input className="form-control" {...register('location')} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('address')}</label>
                                    <input className="form-control" {...register('address')} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('status')}</label>
                                    <select className="form-select" {...register('status')}>
                                        {Object.keys(STATUS_COLORS).map((s) => (
                                            <option key={s} value={s}>
                                                {t(s) || s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">{t('description')}</label>
                                    <textarea className="form-control" rows={3} {...register('description')} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeProjectModal}>
                                    {t('cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? t('saving') : t('save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
